import React, {
  useContext,
  useState,
  useCallback,
  useEffect,
  ReactNode,
  createRef,
} from 'react';
import { AppContext } from '@prisjakt/ui-app-context';
import PanelHeader from '@prisjakt/ui-panel-header';
import { Hideable } from '@prisjakt/ui/hideable';
import { Axis } from '@prisjakt/ui/definitions';
import SearchIcon from 'mdi-react/MagnifyIcon';
import LayoutPicker from './layout-picker';
import { ProductsSlice } from '../../../../types';
import CategoryInformation from '../../../category/components/category-information';
import { LayoutMode } from '../../types';
import SortOptions from './sort-options';
import SearchInput, { useSearchQuery } from './search-input';
import { useViewportContext } from '../../../../components/viewport/context';

import * as S from './styles';

type Props = {
  setLayout: (mode: LayoutMode) => void;
  layout: LayoutMode;
  compareProductsToggle?: ReactNode;
  availableSortsTranslations: Record<string, string>;
  supportedViewModes: ReadonlyArray<LayoutMode>;
  displayProductSearch?: boolean;
} & Pick<
  ProductsSlice,
  | 'productsCount'
  | 'storeCount'
  | 'legalText'
  | 'availableSorts'
  | 'currentSort'
>;

const ProductsHeader = ({
  productsCount,
  storeCount,
  legalText,
  availableSorts,
  availableSortsTranslations,
  currentSort,
  setLayout,
  layout,
  compareProductsToggle,
  supportedViewModes,
  displayProductSearch = true,
}: Props) => {
  const { t } = useContext(AppContext);
  const [query] = useSearchQuery();
  const [showSearch, setShowSearch] = useState(query !== '');
  useEffect(() => setShowSearch(query !== '' || showSearch), [
    query,
    showSearch,
  ]);
  const searchInputRef: React.RefObject<HTMLInputElement> = createRef();
  const { isDesktop } = useViewportContext();
  const onClickSearch = useCallback(() => {
    const visible = query !== '' || !showSearch;
    setShowSearch(visible);
    if (visible && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [setShowSearch, showSearch, query, searchInputRef]);
  return (
    <S.ProductsHeader>
      <PanelHeader divider="bottom">
        <S.PanelHeaderWrapper>
          <S.PanelHeaderRow>
            <SortOptions
              currentSort={currentSort}
              availableSorts={availableSorts}
              availableSortsTranslations={availableSortsTranslations}
            />
            <S.DisplayOptions>
              {supportedViewModes.length > 1 && (
                <>
                  <S.LayoutButtonContainer>
                    {supportedViewModes.includes(LayoutMode.GRID) &&
                      supportedViewModes.length > 1 && (
                        <LayoutPicker
                          active={layout === LayoutMode.GRID}
                          mode={LayoutMode.GRID}
                          onClick={setLayout}
                        />
                      )}
                    {supportedViewModes.includes(LayoutMode.LIST) &&
                      supportedViewModes.length > 1 && (
                        <LayoutPicker
                          active={layout === LayoutMode.LIST}
                          mode={LayoutMode.LIST}
                          onClick={setLayout}
                        />
                      )}
                  </S.LayoutButtonContainer>
                </>
              )}
              {compareProductsToggle ? (
                <>
                  <S.DisplayOptionsDivider />
                  <div>{compareProductsToggle}</div>
                </>
              ) : null}
              {displayProductSearch &&
                (isDesktop ? (
                  <SearchInput
                    showDivider={
                      compareProductsToggle || supportedViewModes.length > 1
                        ? 'before'
                        : undefined
                    }
                  />
                ) : (
                  <>
                    {(compareProductsToggle ||
                      supportedViewModes.length > 1) && (
                      <S.DisplayOptionsDivider />
                    )}
                    <S.SearchInputIcon onClick={onClickSearch}>
                      <S.Tooltip
                        placement="left"
                        title={t('category', 'product-search.placeholder')}
                      >
                        <SearchIcon />
                      </S.Tooltip>
                    </S.SearchInputIcon>
                  </>
                ))}
            </S.DisplayOptions>
          </S.PanelHeaderRow>
          {displayProductSearch && !isDesktop && (
            <S.PanelHeaderRow>
              <Hideable collapse={Axis.Y} visible={showSearch} renderWhenHidden>
                <SearchInput
                  alwaysOpen
                  inputWidth="100%"
                  inputRef={searchInputRef}
                />
              </Hideable>
            </S.PanelHeaderRow>
          )}
        </S.PanelHeaderWrapper>
      </PanelHeader>
      {legalText && (
        <CategoryInformation
          statistics={
            typeof storeCount === 'number'
              ? {
                  productsCount,
                  storesCount: storeCount,
                }
              : undefined
          }
        />
      )}

      <S.ProductCount hidden={legalText || undefined}>
        <span data-botify="ProductCollectionProductCount">
          {t('category', 'products.count', { n: productsCount })}
        </span>
      </S.ProductCount>
    </S.ProductsHeader>
  );
};

export default ProductsHeader;
